import { useState, useContext, useEffect, useRef } from "react";
import { collection, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { EncryptionContext } from "./EncryptionContext.jsx";
import { encryptText, getEncryptionSecret } from "../utils";

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function sortDays(days) {
  return [...days].sort((a, b) => DAYS_OF_WEEK.indexOf(a) - DAYS_OF_WEEK.indexOf(b));
}

function normalizeDays(dayValue) {
  if (!dayValue) return [];
  if (Array.isArray(dayValue)) return dayValue;
  if (typeof dayValue === "string") {
    return dayValue
      .split(",")
      .map((day) => day.trim())
      .filter(Boolean);
  }
  return [];
}

function formatDaysLabel(days) {
  if (!days || (Array.isArray(days) && days.length === 0)) return "";
  if (Array.isArray(days)) return days.join(", ");
  return days;
}

function DayMultiSelect({ selectedDays, onChange, placeholder, ariaLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDay = (day) => {
    if (selectedDays.includes(day)) {
      onChange(sortDays(selectedDays.filter((d) => d !== day)));
    } else {
      onChange(sortDays([...selectedDays, day]));
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="input w-full text-left flex items-center justify-between gap-2"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
      >
        <span>{selectedDays.length > 0 ? selectedDays.join(", ") : placeholder}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-background-light dark:bg-background-dark shadow-lg p-2">
          <ul className="max-h-48 overflow-auto" role="listbox" aria-label={ariaLabel}>
            {DAYS_OF_WEEK.map((day) => (
              <li key={day}>
                <label className="flex items-center gap-2 py-1 cursor-pointer text-text-light dark:text-text-dark">
                  <input
                    type="checkbox"
                    checked={selectedDays.includes(day)}
                    onChange={() => toggleDay(day)}
                    className="h-4 w-4 text-accent focus:ring-accent"
                  />
                  {day}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const getTodayDate = () => new Date().toISOString().split("T")[0];

function ScheduleTab({
  user,
  db,
  selectedCycle,
  tasks,
  setTasks,
  setDecryptionError,
  loadMoreTasks,
  hasMoreTasks,
}) {
  const { passphrase } = useContext(EncryptionContext);
  const [newTask, setNewTask] = useState("");
  const [newTime, setNewTime] = useState("");
  const [newPriority, setNewPriority] = useState("Medium");
  const [newDayOfWeek, setNewDayOfWeek] = useState([]);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTaskText, setEditTaskText] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editPriority, setEditPriority] = useState("Medium");
  const [editDayOfWeek, setEditDayOfWeek] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveConfirmation, setSaveConfirmation] = useState("");

  const addTask = async () => {
    if (!user || !selectedCycle || !newTask.trim()) return;
    setLoading(true);
    try {
      const encryptionSecret = getEncryptionSecret(user, passphrase);
      const encryptedText = await encryptText(newTask, user, encryptionSecret);
      const encryptedTime = newTime ? await encryptText(newTime, user, encryptionSecret) : "";
      const encryptedPriority = await encryptText(newPriority, user, encryptionSecret);
      const dayOfWeekString = newDayOfWeek.length > 0 ? newDayOfWeek.join(",") : "";
      const encryptedDayOfWeek = dayOfWeekString ? await encryptText(dayOfWeekString, user, encryptionSecret) : "";
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      const docRef = await addDoc(collection(db, `${cyclePath}/schedules/shared/tasks`), {
        text: encryptedText,
        time: encryptedTime,
        priority: encryptedPriority,
        dayOfWeek: encryptedDayOfWeek,
        completed: false,
        lastCompletedDate: null,
        createdAt: new Date().toISOString(),
      });
      setTasks([
        {
          id: docRef.id,
          text: newTask,
          time: newTime,
          priority: newPriority,
          dayOfWeek: [...newDayOfWeek],
          completed: false,
          lastCompletedDate: null,
        },
        ...tasks,
      ]);
      setNewTask("");
      setNewTime("");
      setNewPriority("Medium");
      setNewDayOfWeek([]);
      setSaveConfirmation("Task added successfully!");
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error adding task. Please try again.");
      setDecryptionError("Error adding task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startEditTask = (task) => {
    setEditTaskId(task.id);
    setEditTaskText(task.text);
    setEditTime(task.time || "");
    setEditPriority(task.priority || "Medium");
    setEditDayOfWeek(normalizeDays(task.dayOfWeek));
  };

  const saveEditTask = async () => {
    if (!user || !selectedCycle || !editTaskText.trim() || !editTaskId) return;
    setLoading(true);
    try {
      const encryptionSecret = getEncryptionSecret(user, passphrase);
      const encryptedText = await encryptText(editTaskText, user, encryptionSecret);
      const encryptedTime = editTime ? await encryptText(editTime, user, encryptionSecret) : "";
      const encryptedPriority = await encryptText(editPriority, user, encryptionSecret);
      const editDayOfWeekString = editDayOfWeek.length > 0 ? editDayOfWeek.join(",") : "";
      const encryptedDayOfWeek = editDayOfWeekString
        ? await encryptText(editDayOfWeekString, user, encryptionSecret)
        : "";
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      await updateDoc(doc(db, `${cyclePath}/schedules/shared/tasks`, editTaskId), {
        text: encryptedText,
        time: encryptedTime,
        priority: encryptedPriority,
        dayOfWeek: encryptedDayOfWeek,
      });
      setTasks(
        tasks.map((t) =>
          t.id === editTaskId
              ? {
                ...t,
                text: editTaskText,
                time: editTime,
                priority: editPriority,
                dayOfWeek: [...editDayOfWeek],
              }
            : t
        )
      );
      setEditTaskId(null);
      setEditTaskText("");
      setEditTime("");
      setEditPriority("Medium");
      setEditDayOfWeek([]);
      setSaveConfirmation("Task updated successfully!");
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error updating task. Please try again.");
      setDecryptionError("Error updating task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteTask = async (taskId) => {
    if (!user || !selectedCycle) return;
    setLoading(true);
    try {
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      await deleteDoc(doc(db, `${cyclePath}/schedules/shared/tasks`, taskId));
      setTasks(tasks.filter((t) => t.id !== taskId));
      setSaveConfirmation("Task deleted successfully!");
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error deleting task. Please try again.");
      setDecryptionError("Error deleting task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskCompletion = async (taskId, completed) => {
    if (!user || !selectedCycle) return;
    setLoading(true);
    try {
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      const lastCompletedDate = completed ? getTodayDate() : null;
      await updateDoc(doc(db, `${cyclePath}/schedules/shared/tasks`, taskId), {
        completed,
        lastCompletedDate,
      });
      setTasks(
        tasks.map((t) =>
          t.id === taskId ? { ...t, completed, lastCompletedDate } : t
        )
      );
      window.dispatchEvent(
        new CustomEvent("updateTasks", {
          detail: { taskId, completed, lastCompletedDate },
        })
      );
      setSaveConfirmation(`Task marked as ${completed ? "complete" : "incomplete"}!`);
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error updating task completion. Please try again.");
      setDecryptionError("Error updating task completion. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card animate-fade-in w-full mt-0">
      <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">Manage Schedule</h2>
      {error && (
        <div className="mb-4 p-2 bg-red-100 dark:bg-red-900 text-error rounded">
          {error}
        </div>
      )}
      {saveConfirmation && (
        <div className="mb-4 p-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
          {saveConfirmation}
        </div>
      )}
      {loading && (
        <div className="flex justify-center items-center my-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" aria-label="Loading" />
        </div>
      )}
      {!loading && (
        <>
          <div className="mb-6 space-y-4">
            <div>
              <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                New Task
              </label>
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                className="input w-full"
                placeholder="Enter a new task..."
                aria-label="New task input"
              />
            </div>
            <div>
              <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                Time (Optional)
              </label>
              <input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className="input w-full"
                aria-label="Task time input"
              />
            </div>
            <div>
              <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                Priority
              </label>
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value)}
                className="input w-full"
                aria-label="Task priority selection"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                Day of Week (Optional)
              </label>
              <DayMultiSelect
                selectedDays={newDayOfWeek}
                onChange={setNewDayOfWeek}
                placeholder="Select days"
                ariaLabel="Task day of week selection"
              />
            </div>
            <button
              onClick={addTask}
              className="btn-primary"
              disabled={loading || !newTask.trim()}
              aria-label="Add new task"
            >
              Add Task
            </button>
          </div>
          <h3 className="text-lg font-medium text-text-light dark:text-text-dark mb-2">Current Tasks</h3>
          {tasks.length > 0 ? (
            <ul className="space-y-4">
              {tasks.map((task) => (
                <li key={task.id} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  {editTaskId === task.id ? (
                    <>
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={editTaskText}
                          onChange={(e) => setEditTaskText(e.target.value)}
                          className="input w-full"
                          aria-label="Edit task text"
                        />
                        <input
                          type="time"
                          value={editTime}
                          onChange={(e) => setEditTime(e.target.value)}
                          className="input w-full"
                          aria-label="Edit task time"
                        />
                        <select
                          value={editPriority}
                          onChange={(e) => setEditPriority(e.target.value)}
                          className="input w-full"
                          aria-label="Edit task priority"
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                        </select>
                        <DayMultiSelect
                          selectedDays={editDayOfWeek}
                          onChange={setEditDayOfWeek}
                          placeholder="Select days"
                          ariaLabel="Edit task day of week"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={saveEditTask}
                          className="btn-primary text-sm"
                          disabled={loading || !editTaskText.trim()}
                          aria-label="Save edited task"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditTaskId(null)}
                          className="btn-secondary text-sm"
                          disabled={loading}
                          aria-label="Cancel editing task"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="checkbox"
                          checked={task.completed || false}
                          onChange={() => toggleTaskCompletion(task.id, !task.completed)}
                          className="h-5 w-5 text-accent rounded focus:ring-accent"
                          aria-label={`Mark task ${task.text} as ${task.completed ? "incomplete" : "complete"}`}
                        />
                        <span className={task.completed ? "line-through text-neutral" : ""}>
                          {task.text} {task.time && `(${task.time})`} {formatDaysLabel(task.dayOfWeek) && `(${formatDaysLabel(task.dayOfWeek)})`} - {task.priority}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditTask(task)}
                          className="btn-secondary text-sm"
                          disabled={loading}
                          aria-label={`Edit task ${task.text}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="btn-secondary text-error text-sm"
                          disabled={loading}
                          aria-label={`Delete task ${task.text}`}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-neutral">No tasks available. Add a new task above.</p>
          )}
          {hasMoreTasks && (
            <div className="mt-4">
              <button
                onClick={loadMoreTasks}
                className="btn-primary"
                disabled={loading}
                aria-label="Load more tasks"
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ScheduleTab;